import {type UniversalHelpClientOptions as ClientOpts} from '@stripe-internal/universal-help-conversations-client';
import {
  ConversationsApiClient,
  type ConversationsApiClientOptions,
} from '@stripe-internal/conversations-api-client';
import {
  EmailIssueClientUrls,
  type Observability,
  RequestACallClientUrls,
} from '@stripe-internal/conversations-client';
import {
  InlineDataEventBus,
  type InlineDataService,
} from '@stripe-internal/inline-data';
import {TwilioClient} from '@stripe-internal/twilio-conversations-client';

import {Api} from '../../core/lib/api/api';
import {AppConfig, AppProfile} from '../../core/types';
import {isDotComEntryPoint} from '../../core/lib/util/conversationUtils';
import {newInlineDataService} from '../../core/lib/inlineDataService';

/**
 * The transports shared by every Threads conversation. Each conversation gets
 * its own ThreadsConversationClient routing over these; nothing here is scoped
 * to a single conversation.
 */
export type ThreadsTransports = {
  uhClient: ConversationsApiClient;
  chatClient: TwilioClient;
  inlineDataService: InlineDataService;
  inlineDataEventBus: InlineDataEventBus;
};

/**
 * NOTE: conversationsApiUrls / conversationsApiClientOpts mirror the legacy
 * assistant's createClient.ts. That duplication is intentional — do NOT
 * extract a shared helper: createClient.ts belongs to the legacy
 * StripeAssistant and is expected to be deprecated once threads is fully
 * rolled out; keeping the stacks independent lets the legacy path be deleted
 * wholesale. Until then, if you change opts here, update that file too (and
 * vice versa).
 */
export function createThreadsTransports({
  getConfig,
  getProfile,
  getApi,
  getObservability,
}: {
  getConfig: () => AppConfig;
  getProfile: () => AppProfile;
  getApi: () => Api;
  getObservability: () => Observability;
}): ThreadsTransports {
  const {
    audience,
    entryPoint,
    urls: {attachmentDownloadPath},
  } = getConfig();
  const {merchant, featureFlags} = getProfile();

  const emailUrls: EmailIssueClientUrls = {
    emailIssueUrl: '/api/contact-form',
  };

  const racUrls: RequestACallClientUrls = {
    requestACallUrl: '/api/request-a-call',
    verificationCodesUrl: `/api/request-a-call/verification-code?redirectUrl=${document.referrer}`,
    channelsUrl: `/api/eligible-channels/${entryPoint}`,
  };

  const conversationsApiUrls = {
    conversationUrl: '/api/v2/conversations',
    attachmentDownloadPath,
    ...emailUrls,
    ...racUrls,
  };

  const humanAgentGroup = isDotComEntryPoint(entryPoint) ? 'sales' : 'support';

  const deviceToken = featureFlags.enableLiveChatSupportInMobile
    ? new URLSearchParams(window.location.search).get('device_token')
    : null;
  const deviceTokenParam = deviceToken
    ? `&device_token=${encodeURIComponent(deviceToken)}`
    : '';

  const chatUrls = {
    tokenUrl: `/api/chat-token?audience=${audience}&entrypoint=${entryPoint}`,
    conversationUrl: `/api/start-chat?audience=${audience}&entrypoint=${entryPoint}&human_agent_group=${humanAgentGroup}${deviceTokenParam}`,
    ...emailUrls,
    ...racUrls,
  };

  const baseClientOpts: ClientOpts = {
    clientFetch: getApi().fetch,
    observability: getObservability(),
  };

  const chatClientOpts: ClientOpts = {
    ...baseClientOpts,
    urls: chatUrls,
  };

  const conversationsApiClientOpts: ConversationsApiClientOptions = {
    ...baseClientOpts,
    urls: conversationsApiUrls,
    userCountry: merchant?.country,
    enableUhPendingInput: featureFlags.enableUhPendingInput,
    enableRegionHeaderRouting: featureFlags.enableRegionHeaderRouting,
    enableConversationsThreads: featureFlags.enableConversationsThreads,
  };

  return {
    uhClient: new ConversationsApiClient(conversationsApiClientOpts),
    chatClient: new TwilioClient(chatClientOpts),
    inlineDataEventBus: new InlineDataEventBus(),
    inlineDataService: newInlineDataService(getApi().fetch, {
      audience,
      entry_point: entryPoint,
    }),
  };
}
